"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "@@/styles/components/about/HorizontalPartnersCarousel.module.scss";
import { exo2Bold, exo2SemiBold } from "@@/utils/fonts";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";

interface HorizontalAutoCarouselProps {
    title?: string;
    logos?: { src: string; alt: string }[];
    texts?: { text: string }[];
    sectionId?: string;
    className?: string;
}

type Logo = { src: string; alt: string };


type MarqueeVars = React.CSSProperties & {
    ["--marquee-duration"]?: string;
    ["--marquee-shift"]?: string;
};

function isValidGap<T>(arr: T[], key: (x: T) => string, minGap: number): boolean {
    const lastSeen = new Map<string, number>();
    for (let i = 0; i < arr.length; i++) {
        const k = key(arr[i]);
        if (lastSeen.has(k)) {
            const diff = i - (lastSeen.get(k) as number) - 1;
            if (diff < minGap) return false;
        }
        lastSeen.set(k, i);
    }
    if (arr.length > 1 && key(arr[0]) === key(arr[arr.length - 1])) return false;
    return true;
}

function fyShuffle<T>(arr: T[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
}

function shuffleWithMinGap<T>(
    input: T[],
    key: (x: T) => string,
    minGap = 3,
    maxTries = 1500
): T[] {
    if (input.length <= 1) return [...input];

    for (let t = 0; t < maxTries; t++) {
        const candidate = [...input];
        fyShuffle(candidate);
        if (isValidGap(candidate, key, minGap)) return candidate;
    }

    const buckets = new Map<string, T[]>();
    for (const it of input) {
        const k = key(it);
        const list = buckets.get(k);
        if (list) list.push(it);
        else buckets.set(k, [it]);
    }

    const keys = Array.from(buckets.keys()).sort(
        (a, b) => buckets.get(b)!.length - buckets.get(a)!.length
    );

    const out: T[] = [];
    const lastIndex = new Map<string, number>();

    for (let i = 0; i < input.length; i++) {
        const valid = keys.filter((k) => {
            const li = lastIndex.get(k);
            if (li == null) return buckets.get(k)!.length > 0;
            const gap = i - li - 1;
            return gap >= minGap && buckets.get(k)!.length > 0;
        });

        let pickKey: string | undefined;
        if (valid.length) {
            pickKey = valid[(Math.random() * valid.length) | 0];
        } else {
            pickKey = keys.find((k) => buckets.get(k)!.length > 0);
        }

        if (!pickKey) break;
        const keyPicked = pickKey;

        const bucket = buckets.get(keyPicked)!;
        const item = bucket.pop()!;
        out.push(item);
        lastIndex.set(keyPicked, i);

        if (bucket.length === 0) {
            buckets.delete(keyPicked);
            const idx = keys.indexOf(keyPicked);
            if (idx >= 0) keys.splice(idx, 1);
        }
    }

    if (out.length > 1 && key(out[0]) === key(out[out.length - 1])) {
        const swapIdx = out.findIndex((x, i) => i > 0 && key(x) !== key(out[0]));
        if (swapIdx > 0) {
            [out[swapIdx], out[out.length - 1]] = [out[out.length - 1], out[swapIdx]];
        }
    }

    return out;
}

function hasBoundaryRun<T>(arr: T[], key: (x: T) => string, k = 2) {
    if (arr.length < k + 1) return false;
    for (let i = 0; i < k; i++) {
        if (key(arr[i]) !== key(arr[arr.length - k + i])) return false;
    }
    return true;
}

function rotateLeft<T>(arr: T[], n = 1): T[] {
    const k = ((n % arr.length) + arr.length) % arr.length;
    return [...arr.slice(k), ...arr.slice(0, k)];
}

export function buildLogoSequence(logos: Logo[] = [], min = 16): Logo[] {
    const n = logos.length;
    if (n === 0) return [];

    if (n === 1) {
        const out = Array.from({ length: min }, () => logos[0]);
        return out;
    }

    if (n === 2) {
        const out: Logo[] = [];
        for (let i = 0; i < min; i++) out.push(logos[i % 2]);
        return out;
    }

    if (n === 3) {
        const out: Logo[] = [];
        for (let i = 0; i < min; i++) out.push(logos[i % 3]);
        if (out[0].src === out[out.length - 1].src) {
            const first = out.shift()!;
            out.push(first);
        }
        return out;
    }

    let base = [...logos];
    if (n >= min) {
        base = shuffleWithMinGap(base, (l) => l.src, 4);
    }

    const need = Math.max(0, min - base.length);
    let altOrder =
        need > 0
            ? shuffleWithMinGap([...base], (l) => l.src, Math.min(3, Math.max(1, Math.floor(n / 3))))
            : [];
    if (need > 0 && altOrder.every((x, i) => x.src === base[i % base.length].src)) {
        altOrder = [...base].reverse();
    }

    const out: Logo[] = [];
    for (const it of base) {
        const prev = out[out.length - 1];
        out.push(!prev || prev.src !== it.src ? it : (base.find((l) => l.src !== prev.src) ?? it));
    }

    const K = Math.min(5, Math.floor(n / 5));
    let ai = 0;
    const isBoundaryClashIfAppend = (arr: Logo[], cand: Logo) => {
        if (K <= 0) return false;
        const tmp = [...arr, cand];
        if (tmp.length < K) return false;
        for (let i = 0; i < K; i++) {
            if (tmp[i].src !== tmp[tmp.length - K + i].src) return false;
        }
        return true;
    };

    while (out.length < min && altOrder.length > 0) {
        const cand = altOrder[ai % altOrder.length];
        ai++;
        const prev = out[out.length - 1];
        if (prev && prev.src === cand.src) continue;
        if (isBoundaryClashIfAppend(out, cand)) continue;
        out.push(cand);
    }

    const boundaryClash = () => {
        if (K <= 0 || out.length < K) return false;
        for (let i = 0; i < K; i++) {
            if (out[i].src !== out[out.length - K + i].src) return false;
        }
        return true;
    };

    let guard = 0;
    while (boundaryClash() && guard < out.length) {
        const first = out.shift()!;
        out.push(first);
        guard++;
    }

    for (let i = 1; i < out.length; i++) {
        if (out[i].src === out[i - 1].src) {
            const swapIdx = out.findIndex((x, j) => j !== i && x.src !== out[i - 1].src);
            if (swapIdx > -1) [out[i], out[swapIdx]] = [out[swapIdx], out[i]];
        }
    }

    return out;
}


export default function HorizontalAutoCarousel({
                                                   title, logos, texts, sectionId, className,
                                               }: HorizontalAutoCarouselProps) {
    const titleAnim = useAppearOnScroll<HTMLDivElement>({
        from: "fromBottom", duration: 1000, delay: 0, offset: 0,
    });

    const [isPaused, setIsPaused] = useState(false);


    const baseLogos = useMemo(() => buildLogoSequence(logos ?? [], 14), [logos]);

    const useDoubleTrack = baseLogos.length <= 14;

    const marqueeLogos = useMemo(
        () => (useDoubleTrack ? [...baseLogos, ...baseLogos] : baseLogos),
        [baseLogos, useDoubleTrack]
    );

    const SECONDS_PER_LOGO = 3;
    const MIN_S = 12;
    const MAX_S = 60;
    const [durationS, setDurationS] = useState(30);

    const wrapRef = useRef<HTMLDivElement | null>(null);
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [shiftPx, setShiftPx] = useState(0);

    useEffect(() => {
        if (!shiftPx) return;
        const isMobile = window.matchMedia("(max-width: 768px)").matches;
        const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const PX_PER_SEC = prefersReduced ? 30 : (isMobile ? 60 : 100);
        const MIN_S = 12;
        const MAX_S = 300;

        const computed = shiftPx / PX_PER_SEC;
        setDurationS(Math.min(MAX_S, Math.max(MIN_S, computed)));
    }, [shiftPx]);

    useEffect(() => {
        const el = innerRef.current;
        if (!el) return;

        let raf = 0;
        const measureNow = () => {
            const w = el.scrollWidth;
            setShiftPx(useDoubleTrack ? w / 2 : w);
        };
        const schedule = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measureNow);
        };

        schedule();

        const ro = new ResizeObserver(schedule);
        ro.observe(el);
        window.addEventListener("resize", schedule, { passive: true });

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", schedule);
            cancelAnimationFrame(raf);
        };
    }, [marqueeLogos.length]);

    useEffect(() => {
        const target = wrapRef.current;
        if (!target) return;
        const io = new IntersectionObserver(([entry]) => {
            setIsPaused(!entry.isIntersecting);
        }, { threshold: 0 });
        io.observe(target);
        return () => io.disconnect();
    }, []);

    const trackStyle: MarqueeVars = {
        ["--marquee-duration"]: `${durationS}s`,
        ["--marquee-shift"]: `${shiftPx}px`,
    };

    return (
        <section className={`${className} ${styles.carouselWrapper}`} id={sectionId}>
            {title && (
                <h2
                    ref={titleAnim.ref}
                    className={`${styles.title} ${exo2Bold.className} ${titleAnim.className} fs-2 color-dark px-11`}
                    style={titleAnim.style}
                >
                    {title}
                </h2>
            )}

            <div
                ref={wrapRef}
                className={`${styles.scrollContainer} ${isPaused ? styles.paused : ""}`}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                <div ref={innerRef} className={styles.innerScroll} style={trackStyle}>
                    {marqueeLogos.map((logo, idx) => (
                        <div key={`${logo.src}-${idx}`} className={styles.logo}>
                            <Image src={logo.src} alt={logo.alt} width={240} height={120} />
                        </div>
                    ))}

                    {texts && [...texts, ...texts, ...texts, ...texts].map((text, idx) => (
                        <div key={idx} className={styles.text}>
                            <h3 className={exo2SemiBold.className}>{text.text}</h3>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
